/**
 * ReferralCaptureModal — "New referral" (the approved CaptureForm artboard).
 *
 * A two-panel modal (not a route) opened from the Adviser dashboard's
 * greeting row, Adviser only. Left: the fields — Referred by (autocomplete
 * over existing referrers by case-insensitive display name), Prospect,
 * Received via (four-button row with the same icons the card uses), Re (one
 * line), Voice note, Follow up by (defaults to two SA business days from
 * today, editable), the referral-arrangement toggle and, Adviser-only, the
 * "Load as test file" checkbox (CLAUDE.md §33 conventions, "TEST — " prefix).
 * Right: a LIVE ReferralCard preview of exactly what will land in Inbox.
 *
 * "Add to inbox" creates the referral with pipeline_status 'inbox' through
 * referralsApi.createReferral (which creates or reuses the referrer and
 * defaults the follow-up through addBusinessDays).
 *
 * Voice note: reuses VoiceRecorder exactly as SubmitFile does — the audio is
 * attached AS-IS. On submit the recording is uploaded with the existing
 * uploadVoiceNote helper under a `referral-<uuid>` key and the signed URL is
 * stored on the referral row (voice_note_url). No transcription and no AI
 * summary run (removed 29 September 2026 with the client-file summary);
 * `voice_summary` is deprecated, read-only historical data that the referral
 * page still displays where one exists. The data layer journals one short
 * "Voice note recorded." system note.
 *
 * NO CONTACT DETAILS: two names, a channel and a line. There is no phone,
 * email, ID or address field, by design (POPIA minimality).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, MessageCircle, Mail, User, X } from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import VoiceRecorder from '../recorder/VoiceRecorder';
import ReferralCard from '../files/ReferralCard';
import { createReferral, searchReferrers } from '../../api/referralsApi';
import { uploadVoiceNote } from '../../api/storageApi';
import { addBusinessDays } from '../../utils/businessDays';
import { getTodaySASTDateString } from '../../utils/dateHelpers';
import { REFERRAL_CHANNELS, REFERRAL_CHANNEL_LABELS, REFERRAL_NEXT_ACTION_BUSINESS_DAYS } from '../../utils/referrals';
import { returnToDashboard } from '../../utils/returnToDashboard';
import { DialogFrame, FieldLabel, Hint, dialogStyles as ds } from './ReferralDialogChrome';

const CHANNEL_ICONS = { phone: Phone, whatsapp_sms: MessageCircle, email: Mail, in_person: User };

// First name for note attribution — same local convention as the other
// views (no profiles table).
const EMAIL_TO_FIRST_NAME = {
  'marinus@optimumgroup.co.za': 'Marinus',
  'wlourns@gmail.com': 'Wouter',
  'bridgesvix@gmail.com': 'Vicky',
};
const firstNameOf = (email) =>
  EMAIL_TO_FIRST_NAME[(email || '').toLowerCase()] || (email ? email.split('@')[0] : 'User');

function toYmd(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

const defaultFollowUp = () => toYmd(addBusinessDays(new Date(), REFERRAL_NEXT_ACTION_BUSINESS_DAYS));

export default function ReferralCaptureModal({ open, onClose, onCreated }) {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const isAdviser = role === 'adviser';

  const [referrerName, setReferrerName] = useState('');
  const [referrerType, setReferrerType] = useState('client');
  const [hasArrangement, setHasArrangement] = useState(false);
  const [prospectName, setProspectName] = useState('');
  const [channel, setChannel] = useState('phone');
  const [reLine, setReLine] = useState('');
  const [followUpBy, setFollowUpBy] = useState(defaultFollowUp);
  const [isTest, setIsTest] = useState(false);
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState(null);
  const [error, setError] = useState(null);
  const searchTimer = useRef(null);

  // Fresh form every time the modal opens.
  useEffect(() => {
    if (!open) return;
    setReferrerName('');
    setReferrerType('client');
    setHasArrangement(false);
    setProspectName('');
    setChannel('phone');
    setReLine('');
    setFollowUpBy(defaultFollowUp());
    setIsTest(false);
    setVoiceBlob(null);
    setSuggestions([]);
    setShowSuggestions(false);
    setSubmitting(false);
    setStage(null);
    setError(null);
  }, [open]);

  // Referrer autocomplete — debounced, case-insensitive substring search
  // over referrers.display_name (referralsApi.searchReferrers).
  useEffect(() => {
    if (!open) return undefined;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = referrerName.trim();
    if (!q) {
      setSuggestions([]);
      return undefined;
    }
    searchTimer.current = setTimeout(() => {
      searchReferrers(q)
        .then((rows) => setSuggestions(rows || []))
        .catch(() => setSuggestions([]));
    }, 250);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [referrerName, open]);

  const pickReferrer = (r) => {
    setReferrerName(r.displayName);
    setReferrerType(r.referrerType || 'client');
    // An existing arrangement is a fact about the referrer — reflect it.
    if (r.hasReferralArrangement) setHasArrangement(true);
    setShowSuggestions(false);
  };

  // Live preview — exactly the record that will land in Inbox.
  const isTestSubmission = isAdviser && isTest;
  const previewName = (() => {
    const n = prospectName.trim() || 'Prospect';
    return isTestSubmission && !n.startsWith('TEST') ? `TEST — ${n}` : n;
  })();
  const preview = useMemo(
    () => ({
      id: 'preview',
      prospectName: previewName,
      referrer: { displayName: referrerName.trim() || 'referrer' },
      channel,
      reLine: reLine.trim() || null,
      receivedOn: getTodaySASTDateString(),
      pipelineStatus: 'inbox',
      nextActionOn: followUpBy || null,
      progressContactedAt: null,
      progressMeetingAt: null,
      isTest: isTestSubmission,
    }),
    [previewName, referrerName, channel, reLine, followUpBy, isTestSubmission]
  );

  const canSubmit =
    !submitting && referrerName.trim() && prospectName.trim() && REFERRAL_CHANNELS.includes(channel);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(followUpBy)) {
      setError('Choose a follow-up date.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Voice note: upload the recording as-is (no transcription, no
      // summary — voice_summary is deprecated and never written).
      let voiceNoteUrl = null;
      if (voiceBlob) {
        setStage('Uploading voice note…');
        const key = `referral-${crypto.randomUUID()}`;
        voiceNoteUrl = await uploadVoiceNote(key, voiceBlob);
      }

      setStage('Adding to inbox…');
      let name = prospectName.trim();
      if (isTestSubmission && !name.startsWith('TEST')) name = `TEST — ${name}`;
      const actor = { id: user?.id, email: user?.email, firstName: firstNameOf(user?.email), role };
      const created = await createReferral(
        {
          referrerName: referrerName.trim(),
          referrerType,
          hasReferralArrangement: hasArrangement,
          prospectName: name,
          channel,
          reLine: reLine.trim(),
          nextActionOn: followUpBy,
          voiceNoteUrl,
          createdBy: user?.email || null,
          // Defensive: the flag is honoured only for the Adviser (§33.2).
          isTest: isTestSubmission,
        },
        actor
      );
      onCreated && onCreated(created);
      onClose && onClose();
      // Created: close and return to the dashboard with the new card
      // highlighted in Inbox (CLAUDE.md §39). The modal already lives on
      // the Adviser dashboard, so this navigates to the current route —
      // the toast and highlight then behave exactly as for the forms.
      returnToDashboard(navigate, {
        role,
        cardType: 'referral',
        cardId: created.id,
        message: 'Referral captured',
        replace: true,
      });
    } catch (e) {
      setError(e?.message || 'Could not add the referral.');
    } finally {
      setSubmitting(false);
      setStage(null);
    }
  };

  return (
    <DialogFrame open={open} onClose={onClose} busy={submitting} width={860} label="New referral">
      <div style={styles.split}>
        {/* Left: the fields */}
        <div style={styles.left}>
          <div style={styles.titleRow}>
            <div>
              <div style={styles.title}>New referral</div>
              <div style={styles.subtitle}>
                Two names, a channel and a line. No contact details — those stay on your phone.
              </div>
            </div>
            <button type="button" aria-label="Close" style={styles.close} onClick={onClose} disabled={submitting}>
              <X size={16} />
            </button>
          </div>

          <div style={styles.grid2}>
            <div style={{ ...ds.field, position: 'relative' }}>
              <FieldLabel htmlFor="ref-referrer">Referred by</FieldLabel>
              <input
                id="ref-referrer"
                type="text"
                value={referrerName}
                onChange={(e) => { setReferrerName(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder="Start typing — existing referrers autocomplete"
                style={ds.input}
                autoComplete="off"
                disabled={submitting}
              />
              {showSuggestions && suggestions.length > 0 && (
                <ul style={styles.suggest} role="listbox">
                  {suggestions.map((r) => (
                    <li key={r.id}>
                      <button type="button" style={styles.suggestItem} onMouseDown={(e) => e.preventDefault()} onClick={() => pickReferrer(r)}>
                        <span>{r.displayName}</span>
                        <span style={styles.suggestMeta}>
                          {r.hasReferralArrangement ? 'arrangement' : r.referrerType}
                          {r.isTest ? ' · test' : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={ds.field}>
              <FieldLabel htmlFor="ref-prospect">Prospect</FieldLabel>
              <input
                id="ref-prospect"
                type="text"
                value={prospectName}
                onChange={(e) => setProspectName(e.target.value)}
                placeholder="Name or initials"
                style={ds.input}
                disabled={submitting}
              />
            </div>
          </div>

          <div style={ds.field}>
            <FieldLabel>Received via</FieldLabel>
            <div style={styles.seg} role="radiogroup" aria-label="Received via">
              {REFERRAL_CHANNELS.map((c) => {
                const Icon = CHANNEL_ICONS[c];
                const on = channel === c;
                return (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    style={{ ...styles.segBtn, ...(on ? styles.segOn : {}) }}
                    onClick={() => setChannel(c)}
                    disabled={submitting}
                  >
                    <Icon size={14} />
                    {REFERRAL_CHANNEL_LABELS[c]}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={ds.field}>
            <FieldLabel htmlFor="ref-re">Re</FieldLabel>
            <input
              id="ref-re"
              type="text"
              value={reLine}
              onChange={(e) => setReLine(e.target.value)}
              placeholder="One line — what is it about?"
              style={ds.input}
              maxLength={120}
              disabled={submitting}
            />
          </div>

          <div style={styles.grid2}>
            <div style={ds.field}>
              <FieldLabel>Voice note</FieldLabel>
              <VoiceRecorder
                onRecordingComplete={(blob) => setVoiceBlob(blob)}
                disabled={submitting}
              />
              <Hint>Kept with the card and playable from its page.</Hint>
            </div>
            <div style={ds.field}>
              <FieldLabel htmlFor="ref-followup">Follow up by</FieldLabel>
              <input
                id="ref-followup"
                type="date"
                value={followUpBy}
                onChange={(e) => setFollowUpBy(e.target.value)}
                style={ds.input}
                disabled={submitting}
              />
              <Hint>Two working days by default. Edit if you've already agreed a time.</Hint>
            </div>
          </div>

          <label style={styles.toggleRow}>
            <input
              type="checkbox"
              checked={hasArrangement}
              onChange={(e) => setHasArrangement(e.target.checked)}
              style={styles.toggleInput}
              disabled={submitting}
            />
            <span style={{ ...styles.toggle, ...(hasArrangement ? styles.toggleOn : {}) }} aria-hidden="true">
              <span style={{ ...styles.toggleKnob, ...(hasArrangement ? styles.toggleKnobOn : {}) }} />
            </span>
            <span>Referral arrangement in place (fee or reciprocal benefit) — for the conflict-of-interest register</span>
          </label>

          {/* Load as test file — Adviser only, LAST field, as on the other
              two forms (CLAUDE.md §33.2). Forced false for any other role. */}
          {isAdviser && (
            <label style={styles.testRow}>
              <input
                type="checkbox"
                checked={isTest}
                onChange={(e) => setIsTest(e.target.checked)}
                disabled={submitting}
                style={styles.testCheckbox}
              />
              <span>
                <span style={{ fontWeight: 600 }}>Load as test file</span>
                <span style={styles.testHint}>
                  {' '}Test files are excluded from stats and pipeline rules, and can be deleted later. Visible to Adviser only.
                </span>
              </span>
            </label>
          )}

          {error && <div style={ds.error}>{error}</div>}

          <div style={ds.row}>
            <button type="button" style={ds.secondary} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="button" style={ds.primary} onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? stage || 'Adding…' : 'Add to inbox'}
            </button>
          </div>
        </div>

        {/* Right: live card preview */}
        <div style={styles.right}>
          <div style={styles.previewLabel}>Card preview</div>
          <div style={styles.previewCard}>
            <ReferralCard referral={preview} interactive={false} />
          </div>
          <div style={styles.previewNote}>
            Lands in your Inbox column only.
            <br />
            Visible to the team once you pick it up.
          </div>
        </div>
      </div>
    </DialogFrame>
  );
}

const styles = {
  split: { display: 'flex', flexWrap: 'wrap', margin: '-28px -30px -24px' },
  left: {
    flex: '1 1 440px',
    minWidth: 0,
    padding: '28px 30px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  right: {
    flex: '0 0 300px',
    background: 'var(--cp-referral-sheet)',
    borderLeft: '1px solid var(--cp-referral-track)',
    padding: '28px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '18px',
  },
  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' },
  title: { fontSize: '20px', fontWeight: 700, color: 'var(--cp-primary)' },
  subtitle: { fontSize: '12px', color: 'var(--cp-text-muted)', marginTop: '4px' },
  close: {
    background: 'transparent',
    border: 0,
    color: 'var(--cp-text-muted)',
    cursor: 'pointer',
    padding: '4px',
    lineHeight: 0,
  },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '16px', alignItems: 'start' },
  seg: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  segBtn: {
    flex: '1 1 90px',
    height: '44px',
    border: '1.5px solid var(--cp-border)',
    background: 'var(--cp-surface)',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    font: "600 11px 'Montserrat', 'DM Sans', sans-serif",
    color: 'var(--cp-referral-label)',
    cursor: 'pointer',
  },
  segOn: { background: 'var(--cp-referral-ink)', borderColor: 'var(--cp-referral-ink)', color: 'var(--cp-surface)' },
  suggest: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 5,
    listStyle: 'none',
    margin: '4px 0 0',
    padding: 0,
    background: 'var(--cp-surface)',
    border: '1.5px solid var(--cp-border)',
    borderRadius: '6px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
    maxHeight: '180px',
    overflowY: 'auto',
  },
  suggestItem: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 12px',
    border: 0,
    background: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    font: "500 13px 'Montserrat', 'DM Sans', sans-serif",
    color: 'var(--cp-text)',
  },
  suggestMeta: { fontSize: '11px', color: 'var(--cp-text-muted)' },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--cp-text)',
    cursor: 'pointer',
  },
  toggleInput: { position: 'absolute', opacity: 0, width: 0, height: 0 },
  toggle: {
    width: '36px',
    height: '20px',
    borderRadius: '10px',
    background: 'var(--cp-border)',
    position: 'relative',
    flex: '0 0 36px',
    transition: 'background 0.2s',
  },
  toggleOn: { background: 'var(--cp-referral-ink)' },
  toggleKnob: {
    position: 'absolute',
    left: '2px',
    top: '2px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: 'var(--cp-surface)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
    transition: 'left 0.2s',
  },
  toggleKnobOn: { left: '18px' },
  testRow: { display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '12px', cursor: 'pointer' },
  // The global stylesheet makes every input full-width; pin the checkbox.
  testCheckbox: { width: '16px', height: '16px', flex: '0 0 16px', margin: '2px 0 0', padding: 0 },
  testHint: { color: 'var(--cp-text-muted)' },
  previewLabel: {
    fontSize: '9.5px',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: 'var(--cp-referral-ink-soft)',
  },
  previewCard: { width: '240px' },
  previewNote: { fontSize: '11px', color: 'var(--cp-referral-label)', textAlign: 'center', lineHeight: 1.5 },
};
